import {
	ReactElement,
	useState,
	useEffect,
	useMemo
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { DestinationWindow } from "client/Api/Apps/common";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import { ZermeloLiveInformationProvider, InterestedZermeloParty } from "./provider";
import {
	ZermeloScheduleResponseModel,
	ItemsResultStatus,
	ZermeloAppointment
} from "client/Api/Workspace/LiveInformation/Zermelo/zermeloLiveInformation";
import { LiveTileContent } from "../LiveTileContent";
import {
	BusyIndicator,
	WeekSlider,
	WeekView,
	SliderWithToggle
} from "client/Components";
import { useWeekDays, WeekDay } from "client/Hooks";
import { ZermeloCard } from "./ZermeloCard";
import { ScheduleKind } from "./misc";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { ensureNotNil } from "client/Misc/utils";
import styles from "./ZermeloWeekSchedule.module.less";
import { TimeZone } from "client/DateAndTime/timezones";

const {
	Slider,
	Toggle
} = SliderWithToggle;

export type ZermeloWeekScheduleProps = {
	destinationWindow: DestinationWindow;
	connectionId: number;
	provider: ZermeloLiveInformationProvider;
	date: Date;
	onDateChange(date: Date): void;
	onChangeScheduleKind(kind: ScheduleKind): void;
	isTodayButtonAvailable?: boolean;
	timezone: TimeZone;
	addAnalyticsEvent(): void;
};

type LoadingState = {
	isLoading: boolean;
	hasTooManyConsecutiveErrors?: boolean;
	hasGlobalFailure?: boolean;
	isTeacher?: boolean;
	response?: ZermeloScheduleResponseModel;
};

export const ZermeloWeekSchedule = (props: ZermeloWeekScheduleProps): ReactElement => {
	const {
		connectionId,
		destinationWindow,
		provider,
		date,
		onDateChange,
		onChangeScheduleKind,
		isTodayButtonAvailable,
		timezone,
		addAnalyticsEvent
	} = props;

	const [partyId] = useState<Guid>(() => newGuid());
	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	const days: readonly WeekDay[] = useWeekDays(
		date,
		"iso",
		timezone
	);

	if (days.length === 0) {
		throw new Error("No days in the week.");
	}

	const startOfIsoWeek: Date = days[0].date;
	const comparableStartOfWeek: number = startOfIsoWeek.getTime();

	useEffect(
		() => {
			const party: InterestedZermeloParty = {
				partyId,
				resourceId: {
					connectionId,
					date: new Date(comparableStartOfWeek)
				},
				informAboutGlobalRetrievalFailure: (): void => {
					setLoadingState((x) => ({
						...x,
						isLoading: false,
						hasGlobalFailure: true,
						response: undefined
					}));
				},
				informAboutConsecutiveErrors: (): void => {
					setLoadingState((x) => ({
						...x,
						isLoading: false,
						hasTooManyConsecutiveErrors: true
					}));
				},
				updateWeekSchedule: (response: ZermeloScheduleResponseModel, isTeacher: boolean): void => {
					setLoadingState((x) => ({
						...x,
						isLoading: false,
						hasTooManyConsecutiveErrors: false,
						hasGlobalFailure: false,
						isTeacher,
						response
					}));
				}
			};

			setLoadingState((x) => ({
				...x,
				isLoading: true
			}));

			provider.register(party, { weekSchedule: true});

			return () => {
				provider.unregister(party);
			};
		},
		[
			connectionId,
			partyId,
			provider,
			comparableStartOfWeek
		]
	);

	const tileRefreshHandler = (): void => {
		setLoadingState((x) => ({
			...x,
			isLoading: true
		}));
		provider.forceReload();
	};

	const {
		isLoading,
		isTeacher,
		hasTooManyConsecutiveErrors,
		hasGlobalFailure,
		response
	} = loadingState;

	const hasError: boolean = hasGlobalFailure || response?.status !== ItemsResultStatus.Success;

	const items = useMemo(
		(): readonly ZermeloAppointment[] => {
			return response?.items ?? [];
		},
		[response?.items]
	);

	const renderCard = (appointment: ZermeloAppointment): ReactElement => (
		<ZermeloCard
			destinationWindow={destinationWindow}
			isTeacher={ensureNotNil(isTeacher)}
			appointment={appointment}
			timezone={timezone}
			onClick={addAnalyticsEvent}
		/>
	);

	const renderContent = (): ReactElement => {
		if (isLoading) {
			return <BusyIndicator />;
		}

		if (hasTooManyConsecutiveErrors) {
			return <TileRefresh onRefresh={tileRefreshHandler} />;
		}

		if (hasError) {
			return (
				<LiveTileContent.NoResults
					trackingId="has-error"
					icon="gear"
					title={localize("_UnableToRetrieveItems")}
					message={localize("_WeWereUnableToRetrieveTheItems")}
					onRetry={tileRefreshHandler}
				/>
			);
		}

		if (response?.items.length === 0) {
			return (
				<LiveTileContent.NoResults
					trackingId="no-classes"
					icon="calendar"
					title={localize("_NoClassesThisWeek")}
					message={localize("_YouDontHaveAnyClassesThisWeek")}
				/>
			);
		}

		return (
			<LiveTileContent.Scrollable>
				<WeekView
					mode="hours"
					days={days}
					items={items}
					itemKey={getAppointmentKey}
					startDateTimeRetriever={startDateTimeRetriever}
					endDateTimeRetriever={endDateTimeRetriever}
					timezone={timezone}
				>
					{renderCard}
				</WeekView>
			</LiveTileContent.Scrollable>
		);
	};

	return (
		<>
			<div className={styles.sliderContainer}>
				<SliderWithToggle>
					<Slider>
						<WeekSlider
							startDate={startOfIsoWeek}
							onChange={onDateChange}
							mode="iso"
							isTodayButtonAvailable={isTodayButtonAvailable}
							timezone={timezone}
						/>
					</Slider>
					<Toggle
						icon="calendar-day"
						onClick={() => onChangeScheduleKind(ScheduleKind.Day)}
					/>
				</SliderWithToggle>
			</div>
			<LiveTileContent.BusyIndicatorContainer>
				{ renderContent() }
			</LiveTileContent.BusyIndicatorContainer>
		</>
	);
};
ZermeloWeekSchedule.displayName = "ZermeloWeekSchedule";

function getAppointmentKey(appointment: ZermeloAppointment): number | string {
	return appointment.id ?? appointment.startDateTime;
}

function startDateTimeRetriever(appointment: ZermeloAppointment): string {
	return appointment.startDateTime;
}

function endDateTimeRetriever(appointment: ZermeloAppointment): string {
	return appointment.endDateTime;
}
